import { NORMAL_CONFIDENTIALITY_LEVEL_CODING } from '@bariendo/lib/codings';
import { Track, TrackLabel } from '@bariendo/lib/constants';
import { createReference, MedplumClient } from '@medplum/core';
import { Patient, Practitioner, Provenance } from '@medplum/fhirtypes';

/** Distinguishes a track-switch Provenance from any other written on a Patient. */
export const TRACK_SWITCH_ACTIVITY_CODE = 'track-switch';
export const TRACK_SWITCH_ACTIVITY_SYSTEM = 'https://bariendo.com/provenance-activity';

/**
 * Records who switched a patient's track, as a Provenance on the Patient.
 *
 * The authenticated Practitioner is known in Orbit and nowhere downstream:
 * bariendo-be authenticates with a shared Bearer key and cannot say who called
 * it. Shared by the Switch track dialog and the PN card's coverage buttons.
 *
 * **Labelled `N` (normal).** The write policies scope Provenance by
 * confidentiality (`Provenance?_security=%confidentiality-level`), so an
 * unlabelled one is refused: without the label this write 403'd for PN and
 * admin, and the caller swallowed it. Patient Access has Provenance read-only,
 * so it still fails for them.
 */
export async function recordTrackSwitchProvenance(
  medplum: MedplumClient,
  patient: Patient,
  profile: Practitioner,
  from: Track | undefined,
  to: Track
): Promise<Provenance> {
  return medplum.createResource<Provenance>({
    resourceType: 'Provenance',
    meta: { security: [NORMAL_CONFIDENTIALITY_LEVEL_CODING] },
    target: [createReference(patient)],
    recorded: new Date().toISOString(),
    activity: {
      coding: [{ system: TRACK_SWITCH_ACTIVITY_SYSTEM, code: TRACK_SWITCH_ACTIVITY_CODE }],
      text: `Track changed from ${from ? TrackLabel[from] : 'unrecorded'} to ${TrackLabel[to]}`,
    },
    agent: [{ who: createReference(profile) }],
  });
}
